import type { PcmFrame } from './downsample'
import workletUrl from './pcm-worklet.ts?worker&url'

/** Why the microphone could not start. */
export type CaptureError =
  /** The user or the browser blocked microphone access. */
  | 'denied'
  /** No microphone (or none that works). */
  | 'no-microphone'
  /** This browser cannot capture audio (no getUserMedia or AudioWorklet, not a secure context). */
  | 'unsupported'
  | 'failed'

export class CaptureFailed extends Error {
  constructor(readonly reason: CaptureError) {
    super(`microphone: ${reason}`)
    this.name = 'CaptureFailed'
  }
}

/** Whether this browser can capture audio at all (before asking for permission). */
export function canCapture(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    typeof AudioWorkletNode !== 'undefined' &&
    window.isSecureContext
  )
}

function reasonOf(error: unknown): CaptureError {
  const name = error instanceof DOMException ? error.name : ''
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied'
  if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'NotReadableError') {
    return 'no-microphone'
  }
  if (name === 'NotSupportedError') return 'unsupported'
  return 'failed'
}

/**
 * Start the microphone: 16 kHz PCM16 frames of 100 ms with their level, until `stop`. Throws
 * CaptureFailed. Audio stays on the device until the caller sends it to the speech service.
 */
export async function startCapture(
  onFrame: (frame: PcmFrame) => void,
): Promise<{ stop: () => void }> {
  if (!canCapture()) throw new CaptureFailed('unsupported')
  let stream: MediaStream
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    })
  } catch (error) {
    throw new CaptureFailed(reasonOf(error))
  }
  const context = new AudioContext()
  const release = () => {
    for (const track of stream.getTracks()) track.stop()
    void context.close().catch(() => {})
  }
  try {
    await context.resume()
    await context.audioWorklet.addModule(workletUrl)
    const source = context.createMediaStreamSource(stream)
    const node = new AudioWorkletNode(context, 'hugo-pcm16', { numberOfInputs: 1 })
    // Pulled through a muted output, so the graph renders without playing anything back.
    const mute = context.createGain()
    mute.gain.value = 0
    source.connect(node)
    node.connect(mute)
    mute.connect(context.destination)
    node.port.onmessage = (event: MessageEvent<PcmFrame>) => onFrame(event.data)
    let stopped = false
    return {
      stop: () => {
        if (stopped) return
        stopped = true
        node.port.onmessage = null
        source.disconnect()
        node.disconnect()
        release()
      },
    }
  } catch {
    release()
    throw new CaptureFailed('failed')
  }
}
